import React from 'react';
import {Form, Input, Button, Switch} from 'antd';
import {useNavigate} from 'react-router-dom';
import { LeftCircleOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useLoading } from '../../context/LoadingContext';
import { toastError, toastSuccess } from '../../utils/toast';
import {apiAddPartner} from "../../api";

const INPUT_STYLE = {
    width: '90%',
};

const ICON_STYLE = {
    marginLeft: 8,
    fontSize: '16px',
    color: '#999'
};

interface CreatePartnerForm {
    name: string;
    displayName: string;
    cpCode: string;
    subCpCode: string;
    password: string;
    pathCpPubKey: string;
    pathDSPubKey: string;
    pathDSPriKey: string;
    network: string;
    networkAffApi: string;
    status: 'Active' | 'Inactive' | boolean;
    clientSecret: string;
    emailsReport: string[];
    ccEmailsReport: string[];
    bccEmailsReport: string[];
    otpConfirm: string;
    discount: number | string;
    discountUpgrade: number | string;
}

const rules = {
    OTPConfirm: [
        { required: true, message: 'Vui lòng nhập mã code đăng nhập' },
        { min: 6, message: 'Mã code đăng nhập phải có 6 ký tự' },
    ],
    name: [
        { required: true, message: 'Vui lòng nhập tên' }
    ],
    cpCode: [
        { required: true, message: 'Vui lòng nhập mã khách hàng' }
    ],
    discount: [
        { required: true, message: 'Vui lòng nhập chiết khấu' }
    ],
    discountUpgrade: [
        { required: true, message: 'Vui lòng nhập chiết khấu nâng cấp' }
    ]
};

export default function CreatePartner() {
    const [form] = Form.useForm<CreatePartnerForm>();
    const navigate = useNavigate();
    const { setIsLoading } = useLoading();

    const onFinish = async (values: CreatePartnerForm) => {
        try {
            setIsLoading(true);
            let req = {...values};
            req.emailsReport = cleanArray(values.emailsReport || []);
            req.ccEmailsReport = cleanArray(values.ccEmailsReport || []);
            req.bccEmailsReport = cleanArray(values.bccEmailsReport || []);
            req.status = values.status ? "Active" : "Inactive";
            req.discount = Number(values.discount);
            req.discountUpgrade = Number(values.discountUpgrade);
            const resp = await apiAddPartner(req);
            if (resp.status === 200) {
                toastSuccess('Thêm dữ liệu thành công!');
                form.resetFields();
            }
        } catch (error) {
            toastError('Vui lòng kiểm tra lại thông tin!')
        } finally {
            setIsLoading(false);
        }
    };

    const cleanArray = (arr: string[]) => {
        return Array.from(new Set(arr.filter(item => item && item.trim() !== '')));

    }

    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20}}>
                <Button
                    onClick={() => navigate(-1)}
                    icon={<LeftCircleOutlined/>}
                    style={{background: 'gray', color: 'white'}}
                >
                    Trở lại
                </Button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Form
                    style={{ width: '700px' }}
                    form={form}
                    onFinish={onFinish}
                    labelAlign="left"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    initialValues={{
                        status: true,
                        discount: '0',
                        discountUpgrade: '0'
                    }}

                >
                    <Form.Item
                        label="Tên"
                        name="name"
                        rules={rules.name}
                    >
                        <Input
                            placeholder="Nhập tên"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Tên hiển thị"
                        name="displayName"
                    >
                        <Input
                            placeholder="Nhập tên hiển thị"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Mã khách hàng"
                        name="cpCode"
                        rules={rules.cpCode}
                    >
                        <Input
                            placeholder="Nhập mã khách hàng"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Mã khách hàng con"
                        name="subCpCode"
                    >
                        <Input
                            placeholder="Nhập mã khách hàng con"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Chiết khấu"
                        name="discount"
                        rules={rules.discount}
                    >
                        <Input
                            placeholder="Nhập chiết khấu"
                            suffix="%"
                            style={INPUT_STYLE}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, ''); // chỉ lấy số
                                form.setFieldsValue({ discount: value });
                            }}

                        />
                    </Form.Item>

                    <Form.Item
                        label="Chiết khấu nâng cấp"
                        name="discountUpgrade"
                        rules={rules.discountUpgrade}
                    >
                        <Input
                            placeholder="Nhập chiết khấu nâng cấp"
                            suffix="%"
                            style={INPUT_STYLE}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, ''); // chỉ lấy số
                                form.setFieldsValue({ discountUpgrade: value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Mật khẩu"
                        name="password"
                    >
                        <Input.Password
                            placeholder="Nhập mật khẩu"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="PathCpPubKey"
                        name="pathCpPubKey"
                    >
                        <Input
                            placeholder="Nhập PathCpPubKey"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="PathDSPubKey"
                        name="pathDSPubKey"
                    >
                        <Input
                            placeholder="Nhập PathDSPubKey"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="PathDSPriKey"
                        name="pathDSPriKey"
                    >
                        <Input
                            placeholder="Nhập PathDSPriKey"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Network"
                        name="network"
                    >
                        <Input
                            placeholder="Nhập Network"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="NetworkAffApi"
                        name="networkAffApi"
                    >
                        <Input
                            placeholder="Nhập NetworkAffApi"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="ClientSecret"
                        name="clientSecret"
                    >
                        <Input
                            placeholder="Nhập ClientSecret"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.List name="emailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.List name="ccEmailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "CC Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email CC"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.List name="bccEmailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "BCC Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email BCC"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.Item
                        label="Status"
                        name="status"
                        valuePropName="checked"
                    >
                        <Switch></Switch>
                    </Form.Item>

                    <Form.Item
                        label="Mã OTP xác nhận"
                        name="otpConfirm"
                        rules={rules.OTPConfirm}
                    >
                        <Input
                            placeholder="Nhập OTP xác nhận"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button type="primary" htmlType="submit">
                            Thêm
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </>
    );
}
